import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { assessmentService } from '../services/assessmentService';
import AIReadinessGauge from '../components/Dashboard/AIReadinessGauge';
import RecommendationCards from '../components/Recommendations/RecommendationCards';
import LoadingSpinner from '../components/Layout/LoadingSpinner';

const ResultsPage: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();

  const { data: results, isLoading, error } = useQuery(
    ['assessment-results', assessmentId],
    () => assessmentService.getResults(assessmentId!),
    {
      enabled: !!assessmentId,
      retry: 2,
    }
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !results) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Errore nel caricamento dei risultati
          </h1>
          <p className="text-gray-600 mb-6">
            Non siamo riusciti a trovare i risultati del tuo assessment.
          </p>
          <Link
            to="/assessment"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Inizia un nuovo assessment
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900">
            Risultati Assessment
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            {results.companyName} - {results.sector}
          </p>
        </div>

        {/* AI Readiness Score */}
        <div className="mb-12">
          <AIReadinessGauge 
            score={results.aiReadinessScore}
            companyName={results.companyName}
            breakdown={results.breakdown}
          />
        </div>

        {/* Recommendations */}
        <div className="mb-12">
          <RecommendationCards assessmentId={results.assessmentId} />
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Prossimi passi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                1. Scegli il tuo AI Agent
              </h3>
              <p className="text-gray-600">
                Seleziona una delle raccomandazioni qui sopra per generare 
                una roadmap dettagliata di implementazione.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                2. Prenota una consulenza con noi
              </h3>
              <p className="text-gray-600">
               Ti aiuteremo a scegliere il miglior AI Agent per la tua azienda.
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Link
              to="/assessment"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
            >
              Inizia un nuovo assessment
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;